import { Trans, useTranslation } from "@i18n";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import IconComponent from "@CubeComponents/icons";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import CreateIcon from "@Components/StickySidebarViewer/assets/CreateIcon";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { projectTemplateData } from "@Actions/portalSetting";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { convertDate } from "@Services/luxonHelper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { getProjectTemplatesUrl } from "@Features/projects/utils/projectUrls";
import { useNavigate } from "react-router-dom";
import Text from "@CubeComponents/html/Text/Text";
import SlickGrid from "@CubeComponents/html/SlickGrid";
// The legacy SlickGrid keeps its type surface split: column types live in index.tsx, ToolbarItem is
// re-exported from ./types (it is declared in ./ToolBar).
import type { ValidatedColumnDefinition } from "@CubeComponents/html/SlickGrid/index";
import type { ToolbarItem } from "@CubeComponents/html/SlickGrid/types";
import type { SlickgridReactInstance } from "slickgrid-react";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { projectTemplatesUrl } from "@Services/dataManager";
import { RootState } from "@Reducers/index";
import type { TranslatedString } from "@i18n";
import type { ProjectTemplateRow } from "./projectTemplates.helper";
import type { ProjectTemplateControllerRequest } from "@ServerTypes/projectTemplates/projectTemplates.schema";

type ProjectTemplatesProps = {
  /** When set, this component renders only its modals — the grid/header are suppressed. */
  templateData?: unknown;
  setProjectTemplateData: (row?: ProjectTemplateRow) => void;
};

/**
 * Grid handle for the legacy `@CubeComponents/html/SlickGrid`.
 *
 * COMPONENT GAP: unlike SlickGrid1 (which exports `SlickGrid1Ref<T>`), the old SlickGrid keeps its
 * `SlickgridReactInstanceX<T>` handle type un-exported, so consumers cannot name it. `refresh` is
 * intersected in because it lives on that un-exported `SlickgridExtras` half.
 * Fix: export `SlickgridReactInstanceX` (or a `SlickGridRef<T>` alias) from SlickGrid/index.tsx.
 */
type ProjectTemplatesGridRef = SlickgridReactInstance & { refresh?: () => void };

const ProjectTemplates = (props: ProjectTemplatesProps) => {
  const { t } = useTranslation();
  const dataGrid = useRef<ProjectTemplatesGridRef | null>(null);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [isDataEmpty, setIsDataEmpty] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { portalSettingInfo } = useSelector((state: RootState) => state);
  const [toolBarBtnState, setToolBarBtnState] = useState<{ add: boolean; edit: boolean; delete: boolean }>({
    delete: false,
    edit: false,
    add: true,
  });
  const [isGridReady, setIsGridReady] = useState<boolean>(false);
  const defaultTemplateValue = {
    name: "",
    description: "",
  };
  const [TemplateDataToSave, setTemplateDataToSave] = useState(defaultTemplateValue);

  const [query] = useState({});

  const noProjectTemplates = () => {
    return (
      <div className="w-full h-full overflow-hidden flex px-2">
        <div className="flex flex-col h-full w-full justify-center items-center bg-white p-5">
          <IconComponent name="NoProjectTemplatesIllustration" width="15rem" height="15rem" />
          <Text className="mt-5 text-sm">
            <Trans i18nKey="create_project_template" components={{ bold: <Text className="font-semibold" /> }} />
          </Text>
          <div className="flex m-5 gap-5">
            <ButtonComponent
              buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              onClick={() => setOpenAddEditModal(true)}
              disabled={!portalSettingInfo?.projectTemplates?.addProjectTemplates?.enable}
            >
              <span className="flex items-center justify-start">
                <CreateIcon size="15" className="me-5" />
                <Text>{t("Create Template")}</Text>
              </span>
            </ButtonComponent>
          </div>
        </div>
      </div>
    );
  };

  const recordClick = (args: { detail: { args: { row: number; cell: number } } }) => {
    const rowIndex = args.detail.args.row;
    const clickedRow: ProjectTemplateRow = dataGrid.current!.slickGrid.getDataItem(rowIndex);
    const clickedRowId = clickedRow._id;
    if (args.detail.args.cell != 0) {
      props.setProjectTemplateData(clickedRow);
      navigate(getProjectTemplatesUrl(clickedRowId));
    }
  };

  useEffect(() => {
    document.title = t("Portal Settings | Project Templates");
  }, []);

  useEffect(() => {
    if (isGridReady && dataGrid.current) {
      // `SlickDataView.items` is `protected` in @slickgrid-universal, yet this codebase reads it
      // directly here. The assertion is erased — the read is unchanged and the grid is untouched.
      // Fix: use the public `dataView.getItems()`.
      const data = (dataGrid.current.dataView as unknown as { items: ProjectTemplateRow[] }).items;
      if (data.length) {
        setIsDataEmpty(false);
      } else {
        setIsDataEmpty(true);
      }
    }
  }, [isGridReady]);

  const gridDataBound = (val: boolean) => {
    setIsGridReady(val);
  };

  const toolbarClickHandler = (id: string) => {
    switch (id) {
      case "custom_add":
        setOpenAddEditModal(true);
        break;

      case "custom_edit": {
        // `!` is erased: the Edit toolbar button is only enabled with exactly one row selected.
        const selectedData = dataGrid.current!.dataView.getAllSelectedItems<ProjectTemplateRow>()[0]!;
        setTemplateDataToSave({
          name: selectedData.name!,
          description: selectedData.description!,
        });
        setOpenAddEditModal(true);
        setEditMode(true);
        break;
      }

      case "custom_delete":
        setOpenDeleteModal(true);
        break;
    }
  };

  useEffect(() => {
    setToolBarBtnState((prev) => ({
      ...prev,
      add: portalSettingInfo?.projectTemplates?.addProjectTemplates?.enable ?? false,
    }));
  }, [portalSettingInfo]);

  // SlickGrid's `ToolbarItem` declares `text`, but its ToolBar renders `item.name`, so the label
  // field is intersected in rather than switched to `text` (which would render nothing).
  const toolbarOptions: (ToolbarItem & { name: string })[] = [
    {
      name: "Create Template",
      prefixIcon: "add",
      id: "custom_add",
      disabled: !toolBarBtnState.add,
    },
    {
      name: "edit",
      prefixIcon: "editProfile",
      id: "custom_edit",
      disabled: !toolBarBtnState.edit,
    },
    {
      name: "Remove",
      prefixIcon: "delete",
      id: "custom_delete",
      disabled: !toolBarBtnState.delete,
    },
  ];

  const selectionEvent = () => {
    if (!dataGrid?.current) {
      return;
    }
    const totalRowsSelected = dataGrid.current.dataView.getAllSelectedItems<ProjectTemplateRow>().length;

    if (totalRowsSelected == 1) {
      setToolBarBtnState((prev) => ({
        ...prev,
        delete: portalSettingInfo?.projectTemplates?.deleteProjectTemplates?.enable ?? false,
        edit: portalSettingInfo?.projectTemplates?.editProjectTemplates?.enable ?? false,
      }));
    } else {
      setToolBarBtnState((prev) => ({
        ...prev,
        delete: false,
        edit: false,
      }));
    }
  };

  const formatCreatedDate = (data: ProjectTemplateRow) => {
    const dateFormat = convertDate(Number(data.updatedDate), "dateForTooltip");
    const date = data.updatedDate;
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <div className="flex items-center gap-3">
        <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
          <span>{convertDate(Number(date), "dateForText")}</span>
        </ToolTipComponent>
      </div>,
    );
    return container;
  };

  const createdByTemplate = (params: ProjectTemplateRow) => {
    // KNOWN BUG, preserved verbatim: `updatedBy` is dereferenced unconditionally, so a template
    // row without it (the field is not declared on projectTemplateControllerResponse at all —
    // see the ProjectTemplateRow SERVER GAP note) throws while rendering the grid.
    // `!` is erased. Fix: guard, or declare `updatedBy` as required on the response schema.
    const { _id } = params.updatedBy!;
    const container = document.createElement("div");
    renderUserTag(container, { id: _id });
    return container;
  };

  const columnDefinitions = useRef([
    {
      field: "name",
      id: "name",
      filterType: "Text",
      name: t("Name"),
      width: 150,
    },
    {
      field: "description",
      id: "description",
      filterType: "Text",
      name: t("Description"),
      width: 150,
    },
    {
      field: "updatedDate",
      id: "updatedDate",
      name: t("Modified On"),
      minWidth: 120,
      filterType: "Date",
      formatter: (_data: number, _params: number, _a: unknown, _b: unknown, value: ProjectTemplateRow) =>
        formatCreatedDate(value),
    },
    {
      field: "updatedBy.name",
      id: "updatedBy.name",
      name: t("Modified by"),
      minWidth: 150,
      filterType: "Text",
      formatter: (_data: number, _params: number, _a: unknown, _b: unknown, value: ProjectTemplateRow) =>
        createdByTemplate(value),
    },
  ]);

  const getGridComponent = () => (
    <SlickGrid
      ref={dataGrid}
      remoteDataSource={projectTemplatesUrl}
      toolbar={toolbarOptions}
      toolbarClickHandler={toolbarClickHandler}
      onSelectedRowIdsChanged={selectionEvent}
      onClick={recordClick}
      query={query}
      setGridReady={gridDataBound}
      gridOptions={{
        enableSorting: true,
      }}
      // Bridged at the prop: `ValidatedColumnDefinition` is branded and only the `typedColumns()`
      // VALUE import can mint it; adopting that import here would change the emitted module graph.
      columnDefinitions={columnDefinitions.current as unknown as ValidatedColumnDefinition<ProjectTemplateRow>[]}
    />
  );

  // `message` stays a plain string because callers pass both `t(...)` results and raw server
  // `errorMessage`s; the cast is erased and no `untranslated()` call is introduced.
  const showToast = (message: string, status: boolean) => {
    dispatch(
      showToastNotification({
        content: message as TranslatedString,
        type: status ? TOAST_TYPE_SUCCESS : TOAST_TYPE_ERROR,
      }),
    );
  };

  const deleteTemplate = async () => {
    // `!` is erased: the Remove toolbar button is only enabled with exactly one row selected.
    const rowData = dataGrid.current!.dataView.getAllSelectedItems<ProjectTemplateRow>()[0]!;
    const data: ProjectTemplateControllerRequest & { action: "delete" } = {
      data: {
        // `_id` is a plain string on the response schema but `objectIdString` (branded) on the
        // request schema; the cast is erased and bridges the two.
        projectTemplateId: rowData._id as NonNullable<ProjectTemplateControllerRequest["data"]>["projectTemplateId"],
      },
      action: "delete",
    };
    const response = await projectTemplateData(data);
    if (response.data.status) {
      showToast(t("Project Template deleted successfully"), true);
      dataGrid.current!.refresh!();
    } else {
      // KNOWN BUG, preserved verbatim: `errorMessage` is optional on
      // projectTemplateControllerResponse, so a failure without it shows an empty toast.
      // `!` is erased. Fix: fall back to SOMETHING_WENT_WRONG (a runtime change — not done here).
      showToast(response.data.errorMessage!, false);
    }
    setOpenDeleteModal(false);
  };

  const deleteModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={
        <div className="flex justify-normal pb-4 gap-4">
          <ButtonComponent buttonType="gradientButton" onClick={deleteTemplate}>
            <Text>{t("Confirm")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType="borderButton" onClick={handleAddEditModalCLose}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      width="35%"
      headerTitle={t("Delete Project Template")}
      onCancel={handleAddEditModalCLose}
      isVisible={openDeleteModal}
      modelContent={
        <Text className="whitespace-normal">{t("Are you sure you want to delete the selected project template?")}</Text>
      }
    />
  );

  const header = () => (
    <div className="flex flex-col gap-2 ps-4 pb-2 w-full">
      <div className="flex gap-1 items-center">
        <Text className="text-2xl font-semibold" data-testid="projectTemplatesText">
          {t("Project Templates")}
        </Text>
      </div>
      <div>
        <Text className="text-sm" data-testid="projectTemplatesDesc">
          {t("List of all the project templates that can be used to create a project with custom settings")}
        </Text>
      </div>
    </div>
  );

  const addEditModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={footerContent()}
      width="30%"
      headerTitle={editMode ? t("Edit Project Template") : t("Create Project Template")}
      isVisible={openAddEditModal}
      onCancel={handleAddEditModalCLose}
      modelContent={modelContent()}
    />
  );

  const footerContent = () => (
    <div className="mt-5 mb-5 flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleCreateProjectTemplate}
        disabled={!TemplateDataToSave.name}
        data-testid="saveProjectTemplate"
      >
        {editMode ? t("Save") : t("Create")}
      </ButtonComponent>

      <ButtonComponent
        buttonType={BUTTON_TYPES.BORDER_BUTTON}
        onClick={handleAddEditModalCLose}
        data-testid="cancelProjectTemplate"
      >
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const handleCreateProjectTemplate = async () => {
    // `!` is erased: in editMode the Edit button guarantees exactly one selected row.
    const selectedData: Partial<ProjectTemplateRow> = editMode
      ? dataGrid.current!.dataView.getAllSelectedItems<ProjectTemplateRow>()[0]!
      : {};
    // `action: "insert"` on the edit path too is intentional: PROJECT_TEMPLATE_ACTION_PATHS maps
    // both "insert" and "edit" to /saveProjectTemplateData, which upserts on projectTemplateId.
    const data: ProjectTemplateControllerRequest & { action: string } = {
      data: {
        ...TemplateDataToSave,
        projectTemplateId: selectedData._id as NonNullable<
          ProjectTemplateControllerRequest["data"]
        >["projectTemplateId"],
      },
      action: "insert",
    };
    const res = await projectTemplateData(data);
    if (res.data.status) {
      let content;
      if (editMode) {
        content = "The changes have been saved successfully.";
      } else {
        content = t("project_template_created", { name: TemplateDataToSave.name });
      }
      showToast(content, true);
      setTemplateDataToSave(defaultTemplateValue);
      handleAddEditModalCLose();
      if (dataGrid.current) {
        dataGrid.current.refresh!();
      } else {
        setIsDataEmpty(false);
      }
    }
  };

  const handleChange = (args: React.ChangeEvent<HTMLInputElement>) => {
    if (args?.target) {
      const data = args.target.value;
      setTemplateDataToSave({
        ...TemplateDataToSave,
        [args.target.name]: data,
      });
    }
  };

  const modelContent = () => (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Text data-testid="nameLabel">
          {t("Name")}
          <span className="text-red-500">*</span>
        </Text>
        <Text data-testid="nameInputField">
          <TextFieldComponent
            name="name"
            type="text"
            placeholder={t("Add Name")}
            showClearButton
            onChange={handleChange}
            value={TemplateDataToSave.name}
          />
        </Text>
      </div>
      <div className="flex flex-col gap-1">
        <Text data-testid="descriptionLabel">{t("Description")}</Text>
        <span data-testid="descInputField">
          <TextFieldComponent
            name="description"
            type="text"
            placeholder={t("Add Description")}
            showClearButton
            onChange={handleChange}
            value={TemplateDataToSave.description}
          />
        </span>
      </div>
    </div>
  );

  const handleAddEditModalCLose = () => {
    setOpenAddEditModal(false);
    setOpenDeleteModal(false);
    setEditMode(false);
    setTemplateDataToSave(defaultTemplateValue);
  };

  return (
    <div className="w-full flex flex-col h-full overflow-auto">
      {!props.templateData && header()}
      {!props.templateData && !isDataEmpty && getGridComponent()}
      {!props.templateData && isDataEmpty && noProjectTemplates()}
      {openAddEditModal ? addEditModal() : null}
      {openDeleteModal ? deleteModal() : null}
    </div>
  );
};

export default ProjectTemplates;
